import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import clsx from 'clsx'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { rem } from 'polished'
import type { PropsWithChildren, ReactElement, ReactNode } from 'react'
import styled from 'styled-components'
import { tierGradientSecondaryBackgroundStyle, tierNormalContrastColorStyle } from 'styles/utils/tierStyles'

const Icon = styled.div`
  flex-shrink: 0;
  flex-grow: 0;
  display: grid;
  place-items: center;
  ${setSvgIconSize('S')}
`

const EndContent = styled.div`
  flex-shrink: 0;
  display: flex;

  &:empty { display: none; }
`

const Row = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  min-width: 0;
`

/**
 * Keeps the message and anything inline with it (a `Details` link, say) together as one flex item, so
 * `Row`'s `space-between` separates the message from `EndContent` rather than splitting the message's
 * own parts apart.
 */
const Message = styled(BodyText)`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${rem(4)};
  min-width: 0;
`

/* Positioned so a trailing control can stretch its own hit area across the whole bar, which is how
   the flash strip makes the bar tappable on mobile without nesting a second button inside it. */
const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
  min-height: ${rem(48)};
  padding-block: ${rem(8)};
  padding-inline: ${rem(16)} ${rem(8)};
  border-radius: ${props => props.theme.borderRadius.L};

  &.compact {
    min-height: ${rem(32)};
    border-radius: 0;
  }

  /* Without an interactive trailing control there's no button inset to absorb, so pad the end fully. */
  &:not(:has(${EndContent} button), :has(${EndContent} a)) {
    padding-inline-end: ${rem(16)};
  }

  ${tierGradientSecondaryBackgroundStyle}
  ${tierNormalContrastColorStyle}
`

interface Props extends PropsWithChildren {
  tier: App.LoyaltyTier
  /** Leading icon. Absent by default — this banner is usually text-first. */
  icon?: ReactElement | false
  /** Trailing control, e.g. an apply/remove chip. */
  endContent?: ReactNode
  className?: string
  density?: 'comfortable' | 'compact'
}

/**
 * A compact tier-coloured bar offering a stay upgrade on a rate, with a trailing control.
 */
function LoyaltyTierStayUpgradeMessageBanner({
  tier,
  icon,
  endContent,
  className,
  children,
  density = 'comfortable',
}: Props) {
  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <Container
      className={clsx(`tier-${tier}`, className, { compact: density === 'compact' })}
    >
      {!!icon && <Icon>{icon}</Icon>}
      <Row>
        <Message variant={density === 'compact' ? 'small' : 'medium'} weight="bold">{children}</Message>
        {!!endContent && <EndContent>{endContent}</EndContent>}
      </Row>
    </Container>
  </ProductPaletteProvider>
}

export default LoyaltyTierStayUpgradeMessageBanner
